import { IS_WEB } from 'constants/platform'
import React, { useMemo, useState } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { ProgramActionRow } from 'pages/Program/subcomponents/ProgramActionRow'

import { Page } from 'partials/Page'
import { UpspaceHeader } from 'partials/UpspaceHeader'
import { AddStepButton } from 'partials/WorkoutEditorForm/subcomponents/WorkoutStepsForm/AddStepButton'

import { Box } from 'widgets/Box'
import { FilledButton } from 'widgets/Button/FilledButton'
import { FlatList } from 'widgets/FlatList'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'
import { Title } from 'widgets/Title'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { usePosts } from 'hooks/usePosts'
import { useProgram } from 'hooks/useProgram'
import { useProgramSubscriptions } from 'hooks/useSubscriptions'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { getProgramLink } from 'utils/links/links'
import { handleShareAction } from 'utils/sharing'

import { useCreatePostModal } from 'contexts/CreatePostModalContext'
import { useDialogModal } from 'contexts/DialogModalContext'

import { Post } from 'types/post'
import { ProgramType } from 'types/program'

type ProgramPageProps = {
  route: any
  navigation: any
}

export const ProgramPage = ({ route, navigation }: ProgramPageProps) => {
  const programId = 'NdI3c74G0UwcZXsK0y8y' // route?.params?.programId

  const insets = useSafeAreaInsets()
  const { width: maxWidth, height: maxHeight } = useMaxWidth()

  const { data: program, updateProgram, isLoading: isProgramLoading } = useProgram(programId)

  const { data: actions } = usePosts({ postIds: program?.actions ?? [] })

  const { data: currentUser } = useCurrentUser()
  const { data: user, isLoading: isUserLoading } = useUser({ userId: program?.trainer?.id })

  const { data: programSubscriptions } = useProgramSubscriptions({ programId })

  const subscriberIds = useMemo(() => {
    return programSubscriptions?.map((s) => s.user.id) ?? []
  }, [programSubscriptions])

  const isCreator = true // currentUser?.id === user?.id
  const isSubscriber = subscriberIds.includes(currentUser?.id)
  const isViewer = !isCreator && !isSubscriber

  const menuOptions = useMemo(() => {
    const options = ['Copy Program Link', 'Invite to Program']

    if (isCreator) {
      options.push('Duplicate Program')
      options.push('Edit Program')
      options.push('Delete Program')
    }

    if (isSubscriber) {
      options.push('Unsubscribe from Program')
    }

    return options
  }, [isCreator, isSubscriber])

  const actionOptions = [
    'Add to Top',
    'Add to Bottom',
    'Move Up One',
    'Move Down One',
    'Edit Workout',
    'Remove Workout from Program',
  ]

  const [isMenuOptionsModalVisible, setIsMenuOptionsModalVisible] = useState(false)
  const [actionOptionsIndex, setActionOptionsIndex] = useState<number>(0)

  const onShowMenuOptions = () => {
    setIsMenuOptionsModalVisible(true)
  }

  const onCloseMenuOptions = (): void => {
    setIsMenuOptionsModalVisible(false)
  }

  const handleShareProgramOption = async (): Promise<void> => {
    const url = getProgramLink(user?.username as string)
    const message = `Check out ${user?.firstName} ${user?.lastName}'s Program!`
    await handleShareAction(url, message, navigation, links.SHARE_GROUP_PAGE, user?.username)
  }

  const onMenuOptionSelect = (index: number) => {
    switch (index) {
      case 0:
        //
        break
      case 1:
        //
        break
      case 2:
        //
        break
      case 3:
        //
        break
      case 4:
        //
        break
      default:
        break
    }
  }

  const moveToTop = (index: number) => {
    if (!program?.actions?.length || index === 0) return
    const tempActions = program.actions
    tempActions.splice(0, 0, tempActions[index])
    tempActions.splice(index + 1, 1)
    updateActions(tempActions)
  }

  const moveToBottom = (index: number) => {
    if (!program?.actions?.length || index === program?.actions?.length - 1) return
    const tempActions = program.actions
    tempActions.splice(tempActions.length, 0, tempActions[index])
    tempActions.splice(index, 1)
    updateActions(tempActions)
  }

  const swapActions = (index: number) => {
    if (!program?.actions?.length || index < 0 || index >= program?.actions?.length - 1) return
    const tempActions = program.actions
    const temp = tempActions[index]
    tempActions[index] = tempActions[index + 1]
    tempActions[index + 1] = temp
    updateActions(tempActions)
  }

  const deleteAction = (index: number) => {
    if (!program?.actions) return
    const tempActions = program.actions
    tempActions.splice(index, 1)
    updateActions(tempActions)
  }

  const onActionOptionSelect = (index: number) => {
    switch (index) {
      case 0:
        moveToTop(actionOptionsIndex)
        break
      case 1:
        moveToBottom(actionOptionsIndex)
        break
      case 2:
        swapActions(actionOptionsIndex - 1)
        break
      case 3:
        swapActions(actionOptionsIndex)
        break
      case 4:
        // Edit workout
        break
      case 5:
        deleteAction(actionOptionsIndex)
        break
      default:
        break
    }
  }

  const createPostModalRef = useCreatePostModal()
  const dialogModalRef = useDialogModal()

  const onShowActionMenuOptions = () => {
    dialogModalRef?.current?.present({
      options: actionOptions,
      onOptionSelect: (index: number) => {
        onActionOptionSelect(index)
      },
    })
  }

  const handleAddActions = (ids: string[]) => {
    if (!program) return
    const selectedActions = program.actions ? [...program.actions, ...ids] : [...ids]
    updateActions(selectedActions)
  }

  const updateActions = (actionIds: string[]) => {
    if (!program) return
    updateProgram.mutate({ ...program, actions: actionIds })
  }

  const onAddAction = () => {
    createPostModalRef?.current?.present({
      programId,
      onAdd: handleAddActions,
    })
  }

  return (
    <Page
      withScroll
      withNav={!IS_WEB}
      isLoading={isProgramLoading || isUserLoading}
      navigation={navigation}
      backgroundColor='white'
    >
      <UpspaceHeader
        params={{}}
        user={user ?? {}}
        program={program}
        type={ProgramType.PROGRAM}
        onShare={handleShareProgramOption}
        onBackPress={navigation.goBack}
        onOptionsMenuPress={onShowMenuOptions}
      />

      <Separator />

      <Box testID='ProgramListContainer'>
        <Box testID='ProgramListHeaderContainer' flexDirection='row' padding='md' align='center'>
          <Box flex={1} flexDirection='row' align='center' margin={{ right: 'sm' }}>
            <Title color='v2black' bold numberOfLines={1}>
              Full Program
              <Text fontSize='md' color='greyDarkest' numberOfLines={1}>
                {actions?.length ? ` (${actions?.length} Actions)` : ''}
              </Text>
            </Title>
          </Box>
          {actions?.length && isCreator ? (
            <Box>
              <AddStepButton hideSeparator onPress={onAddAction} />
            </Box>
          ) : null}
        </Box>

        <Box testID='ProgramListContentContainer'>
          {(isCreator || isSubscriber) && (
            <Box
              height={actions?.length ? maxHeight - (IS_WEB ? 75 : 130) : undefined}
              width={maxWidth}
            >
              {!actions && <LoadingSpinner />}
              {actions?.length ? (
                <FlatList<Post>
                  keyExtractor={(p) => p.id}
                  data={actions ?? []}
                  contentContainerStyle={{ paddingBottom: insets.bottom }}
                  renderItem={({ item, index }) => {
                    return (
                      <ProgramActionRow
                        data={item}
                        position={index + 1}
                        onActionPress={() => {
                          setActionOptionsIndex(index)
                          onShowActionMenuOptions()
                        }}
                      />
                    )
                  }}
                />
              ) : null}
            </Box>
          )}

          {!actions?.length && isCreator && (
            <Box testID='AddActionsContainer' height={240} align='center' justifyContent='center'>
              <FilledButton
                height={60}
                title='Add Action(s)'
                width={maxWidth * 0.9}
                borderRadius='smd'
                onPress={onAddAction}
              />
              <Box align='center' justifyContent='center' padding='lg'>
                <Text fontSize='sm' color='greyDarker' align='center'>
                  You don’t have any actions added to this program. Click above to add a new or
                  existing action.
                </Text>
              </Box>
            </Box>
          )}

          {isViewer && (
            <Box testID='JoinProgramContainer' height={150} align='center' justifyContent='center'>
              <Text color='greyDarkest' align='center' bold>
                Join Program
              </Text>

              <Box
                align='center'
                justifyContent='center'
                padding={{ top: 'sm', bottom: 'xl', horizontal: 'lg' }}
              >
                <Text fontSize='sm' color='greyDarker' align='center'>
                  By joining you get access to curated list of content & exclusive program feed,
                  leaderboard, and group chat
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        testID='MenuOptionsModal'
        isVisible={isMenuOptionsModalVisible}
        onClose={onCloseMenuOptions}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={async () => {
                onMenuOptionSelect(index)
                setIsMenuOptionsModalVisible(false)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={onCloseMenuOptions} />
      </Modal>
    </Page>
  )
}

// TODO: Example to use on Group settings page
// const deleteProgramModalData = {
//   title: 'Are you sure you want to delete this program?',
//   actions: {
//     primary: {
//       title: 'Delete it!',
//       onPress: () => {
//         console.log('@@ primary action')
//       },
//     },
//     secondary: {
//       title: 'No! Take me back.',
//       onPress: () => {
//         console.log('@@ secondary action')
//       },
//     },
//   },
// }
